import { SecureMessageContainer } from '@app.styled';
import React, { useCallback, useState } from 'react';
import { MessageCenter, MCContextProvider } from '@fmc-digital/securemessages';
import { selectSelectedLoanNumber } from '@store/selectors/widgets.selectors';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery, useTheme } from '@mui/material';
import { withHeaderFooter } from '@components_v2/HOCs/withHeaderFooter/withHeaderFooter';
import { auth, config } from '../../../config';
import { v4 } from 'uuid';
import { selectFeatureFlag } from '@store/reducers/config.reducer';
import { RootState } from '@store';
import { logApiEvent, logUserEvent } from '@analytics/amplifyLogging';
import { Identifier } from '@types';
import { MessageCenterNavigationProps } from '@components_v2/data';
import { useNavigate } from 'react-router-dom';
import { decrementUnreadCount } from '@store/actions/unreadCount.actions';
import { AlertBase, StackBase, TypographyBase } from '@fmc-digital/ui';
import { useHomeContext } from '@components_v2/context/MyHomeContext';
import { UIConstants } from '@constants';

export const MessageCenterContainer = withHeaderFooter(
    () => {
        const [showInfoBanner, setShowInfoBanner] = useState(true);
        const isMCNeeded = useSelector((state: RootState) => selectFeatureFlag(state, 'messageCenter'));
        const unreadCount = useSelector((state: RootState) => state.unreadCount.unreadCount);
        const token = auth.isNotDefined ? null : auth.getOkta().getAccessToken();
        const theme = useTheme();
        const isMobileView = useMediaQuery(theme.breakpoints.between('xs', 'md'));
        const correlationId = v4();
        const currentLoan: Identifier = useSelector(selectSelectedLoanNumber);
        const navigate = useNavigate();
        const dispatch = useDispatch();
        const context = useHomeContext();
        const secureMessageApiProps: any = {
            apiUrl: `${config.env('secureMessagesUrl')}`,
            authToken: `Bearer ${token}`,
            xApiKey: config.env('xApiKey'),
            xCorrelationId: correlationId,
            selfServDocumentsUrl: `${config.env('selfServDocumentsUrl')}`
        };

        const logUserEventHandler = useCallback((action: string, description: string) => {
            logUserEvent({
                type: 'Message Center',
                description: description,
                action: action,
                loanNumber: currentLoan?.identifier
            });
        }, []);
        React.useEffect(() => {
            if (!isMCNeeded) {
                navigate('/', { replace: true });
            }
        }, [isMCNeeded, navigate]);

        const handleDecrement = () => {
            dispatch(decrementUnreadCount());
        };

        const clickedHandler = () => {
            context.accountDetailSSO(`${config.env('myLoanBaseUrl')}/LoanService/SSOPost`).catch(console.log);
        };
        const onCloseHandler = () => {
            setShowInfoBanner(false);
        };
        if (!isMCNeeded) return null;
        return (
            <>
                <SecureMessageContainer id="message_container" style={{ backgroundColor: isMobileView ? '#FEFEFE' : '', height: '100%' }}>
                    <StackBase flexGrow={1} flexDirection={'column'}>
                        {showInfoBanner && (
                            <StackBase id="info-msg-banner" padding={isMobileView ? '1rem 1rem 1rem 1rem ' : '0 0 2rem 2rem'}>
                                <AlertBase
                                    key="messages-info"
                                    id="messages-info-banner"
                                    severity="info"
                                    sx={{
                                        alignItems: 'start'
                                    }}
                                    onClose={onCloseHandler}
                                >
                                    <TypographyBase variant="caption" color="primary.dark">
                                        {UIConstants.messageCentreInfoText1}
                                    </TypographyBase>
                                    <TypographyBase
                                        variant="caption-bold"
                                        onClick={clickedHandler}
                                        color="text.secondary"
                                        sx={{
                                            textDecoration: 'underline',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {UIConstants.messageCentreInfoText2}
                                    </TypographyBase>
                                </AlertBase>
                            </StackBase>
                        )}
                        <MCContextProvider
                            secureMessageApiProps={secureMessageApiProps}
                            logUserEventHandler={logUserEventHandler}
                            logAPIEventHandler={logApiEvent}
                            isRenderedFromACP={true}
                            updateReadCount={handleDecrement}
                            unReadCount={unreadCount}
                        >
                            <MessageCenter />
                        </MCContextProvider>
                    </StackBase>
                </SecureMessageContainer>
            </>
        );
    },
    { ...MessageCenterNavigationProps }
);
